import type { ReactNode } from 'react'
import { LocaleEnum } from '@systemeio/ui-frontend'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { usePublicationRelativeDate } from 'shared/hooks/use-community-relative-date'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import useUser from 'shared/hooks/use-user'
import EllipseIcon from 'shared/icons/ellipse-icon'
import { UserInfoPopover } from 'modules/community/components/user-info-popover/user-info-popover'
import { getReadableMemberWorkspaceRole } from 'modules/community/enums/member-enum'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import type { PostInterface, SearchPostInterface } from 'modules/community/types/post-interface'

interface AuthorInfoProps {
  post: PostInterface | SearchPostInterface
  highlightedAuthorHTML?: string
}

export const PostAuthorInfo = ({ post, highlightedAuthorHTML }: AuthorInfoProps): ReactNode => {
  const { t } = useTranslation()
  const { author, userId, scheduledTo, publishedAt, createdAt, status, topic } = post
  const {
    query: { communityPath },
  } = useRouter()
  const { user } = useUser()

  const locale = user?.dashboardLocale ?? LocaleEnum.ENGLISH
  const isScheduled = status !== PostStatusEnum.Approved && scheduledTo
  const scheduledFormatted = useFormatDateTime(isScheduled ? scheduledTo : null, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    locale,
  })
  const absoluteDate = useFormatDateTime(isScheduled ? scheduledTo : (publishedAt ?? createdAt))
  const publishedRelative = usePublicationRelativeDate(
    isScheduled ? null : (publishedAt ?? createdAt),
  )

  const time = isScheduled ? scheduledFormatted?.formatted : publishedRelative

  return (
    <div className="inline-flex h-fit min-w-0 items-start gap-2.5 text-text-secondary">
      <UserInfoPopover userId={userId} className="shrink-0">
        <img
          data-testid="post-author-avatar"
          src={author?.avatarUrl || '/default_post_author_avatar.png'}
          alt={author?.displayName}
          className="size-10 rounded-full"
          width={40}
          height={40}
        />
      </UserInfoPopover>
      <div className="flex w-full min-w-0 flex-col *:h-5">
        <div className="flex items-center gap-1">
          <span
            data-testid="post-author-name"
            className="max-w-37.5 truncate text-text-primary md:max-w-sm"
            dangerouslySetInnerHTML={{
              __html: highlightedAuthorHTML
                ? DOMPurify.sanitize(highlightedAuthorHTML)
                : author?.displayName || '',
            }}
          />
          <EllipseIcon />
          {author?.workspaceRole && (
            <span className="self-start rounded-sm border border-transparent bg-brand-primary/[0.07] px-1.25 text-[10px]/5 text-brand-primary">
              {t(getReadableMemberWorkspaceRole(author.workspaceRole))}
            </span>
          )}
        </div>
        <div className="mt-auto flex w-full flex-row flex-nowrap gap-1">
          <span
            data-testid="post-author-date"
            className="text-nowrap text-text-secondary"
            title={absoluteDate?.title}
          >
            {time}
          </span>
          <span className="text-nowrap text-text-secondary">{t('community.pages_on')}</span>{' '}
          <Link
            data-testid="post-author-topic-link"
            className="inline-block truncate align-bottom font-semibold text-text-primary"
            href={`/community/${communityPath}/${topic.path}`}
            shallow
          >
            #{topic.name}
          </Link>
        </div>
      </div>
    </div>
  )
}
